'use client'

import { useEffect } from 'react'
import { Button, Dialog, Text } from '@vinted/web-ui'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { clickEvent } from '_libs/common/event-tracker/events'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import FaqEntryUrl from 'components/FaqEntryUrl'
import { FaqEntryType } from 'constants/faq-entry'

type Props = {
  isEmailSharingModalVisible: boolean
  onSetIsEmailSharingModalVisible: (isVisible: boolean) => void
  onSendClick: () => void
}

const EmailSharingModal = ({
  isEmailSharingModalVisible,
  onSetIsEmailSharingModalVisible,
  onSendClick,
}: Props) => {
  const translate = useTranslate('conversation.email_sharing_modal')
  const { track } = useTracking()

  useEffect(() => {
    if (!isEmailSharingModalVisible) return

    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.PersonalInformationModalShow,
      }),
    )
  }, [track, isEmailSharingModalVisible])

  const handleLearnMoreClick = () => {
    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.PersonalInformationModalLearnMore,
      }),
    )
  }

  const hideModal = () => {
    onSetIsEmailSharingModalVisible(false)
  }

  const handleEditClick = () => {
    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.PersonalInformationModalChangeResponse,
      }),
    )

    hideModal()
  }

  const handleSendClick = () => {
    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.PersonalInformationModalSendAnyway,
      }),
    )

    onSendClick()
    hideModal()
  }

  const getLearnMoreLink = () => (
    <FaqEntryUrl
      key="learn-more-link"
      type={FaqEntryType.SpamPhishing}
      render={url => (
        <a href={url} target="_blank" rel="noreferrer" onClick={handleLearnMoreClick}>
          {translate('learn_more_link')}
        </a>
      )}
    />
  )

  return (
    <Dialog
      show={isEmailSharingModalVisible}
      testId="email-sharing-modal"
      title={translate('title')}
      body={
        <Text as="span" text={translate('body', { learn_more_link: getLearnMoreLink() })} html />
      }
      actions={[
        {
          text: translate('actions.edit'),
          callback: handleEditClick,
          style: Button.Styling.Filled,
          testId: 'email-sharing-modal--edit-button',
        },
        {
          text: translate('actions.send'),
          callback: handleSendClick,
          testId: 'email-sharing-modal--send-button',
        },
      ]}
    />
  )
}

export default EmailSharingModal
